import { Box, Flex, FormControl, FormErrorMessage, FormLabel, Stack, Text } from '@chakra-ui/react';
import { Controller, Control, FieldErrors, useWatch } from 'react-hook-form';
import Asterisk from '@/components/common/Asterisk';
import { useGetCategories, useGetSubCategories } from '@/services/addProduct';
import { type FormData } from '@/services/addProduct/types';
import CustomSelect from '@/components/common/CustomSelect';
import { useEffect, useState } from 'react';

interface CategorySelectionSectionProps {
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  id?: string;
}

interface Category {
  id: string | number;
  name: string;
}

interface SubCategory {
  id: string | number;
  name: string;
}

const CategorySelectionSection = ({ control, errors, id }: CategorySelectionSectionProps) => {
  const [categoryOptions, setCategoryOptions] = useState<{ id: string; label: string }[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<{ id: string; label: string }[]>([]);

  const { data: categories } = useGetCategories();
  const selectedCategory = useWatch({
    control,
    name: 'categorySelection.category',
  });
  const isSubCategoryDisabled = !selectedCategory;
  const { data: subCategories } = useGetSubCategories(Number(selectedCategory));

  useEffect(() => {
    if (categories) {
      setCategoryOptions(
        categories.map((category: Category) => ({
          id: category.id.toString(),
          label: category.name,
        })),
      );
    }
  }, [categories]);

  useEffect(() => {
    if (subCategories && subCategories.length > 0) {
      setSubCategoryOptions(
        subCategories.map((subCategory: SubCategory) => ({
          id: subCategory.id.toString(),
          label: subCategory.name,
          searchLabel: subCategory.name,
        })),
      );
    } else {
      setSubCategoryOptions([]); // Clear options if no sub-categories or error
    }
  }, [subCategories]);

  return (
    <Box
      border={'1px solid'}
      borderColor={'border.default60'}
      bg={'background.secondary'}
      borderRadius={'lg'}
    >
      <Flex p={4} direction="column" gap={4}>
        <Text fontSize={'md'} fontWeight={'semibold'}>
          {id ? 'Edit Product' : 'Add Product'}
        </Text>
        <Stack direction={{ base: 'column', md: 'row' }} spacing={4} w="100%">
          <FormControl isInvalid={!!errors.categorySelection?.category}>
            <FormLabel
              fontSize={'sm'}
              color={errors.categorySelection?.category ? 'status.error' : 'text.secondary'}
            >
              Select Category
              <Asterisk />
            </FormLabel>
            <Controller
              name="categorySelection.category"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  options={categoryOptions}
                  placeholder="Select Category"
                  selectedValue={field.value}
                  onChange={(value) => field.onChange(value)}
                  size="md"
                  isFullWidth={true}
                  isSearchable={true}
                />
              )}
            />
            <FormErrorMessage>{errors.categorySelection?.category?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.categorySelection?.subCategory}>
            <FormLabel
              fontSize={'sm'}
              color={errors.categorySelection?.subCategory ? 'status.error' : 'text.secondary'}
            >
              Select Sub-Category
              <Asterisk />
            </FormLabel>
            <Controller
              name="categorySelection.subCategory"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  options={subCategoryOptions}
                  placeholder={
                    isSubCategoryDisabled ? 'Select category first' : 'Select Sub-Category'
                  }
                  selectedValue={field.value}
                  onChange={(value) => field.onChange(value)}
                  size="md"
                  isFullWidth={true}
                  isSearchable={true}
                  isDisabled={isSubCategoryDisabled}
                  noOptionsMessage={isSubCategoryDisabled ? '' : 'No sub-categories available'}
                />
              )}
            />
            <FormErrorMessage>{errors.categorySelection?.subCategory?.message}</FormErrorMessage>
          </FormControl>
        </Stack>
      </Flex>
    </Box>
  );
};

export default CategorySelectionSection;
